import { ComponentPropsWithoutRef, useMemo } from 'react';
import { cx } from '@linaria/atomic';
import {
  SKELETON_BASE,
  SKELETON_CIRCLE,
  SKELETON_PULSE,
  SKELETON_RECTANGLE,
  SKELETON_SHIMMER,
} from '../loaders.styles';
import { useSkeletonLoader } from './skeleton.provider';
import { SkeletonLoaderProps } from './types';
import { generateSizeVariability } from './utils';

type Props<T extends boolean = false> = SkeletonLoaderProps<T> &
  Omit<ComponentPropsWithoutRef<T extends true ? 'span' : 'div'>, keyof SkeletonLoaderProps>;

const sanitizeCssValue = (value?: string) => value?.trim().replace(/;+$/, '');

export const SkeletonLoader = <T extends boolean = false>({
  width = '100%',
  height = '100%',
  shape = 'rectangle',
  count,
  distance,
  path,
  animation = 'pulse',
  duration = 1,
  children,
  isText,
  widthVariability,
  heightVariability,
  isLoading,
  isList,
  className,
  style,
  ...rest
}: React.PropsWithChildren<Props<T>>) => {
  const context = useSkeletonLoader();

  const randomWidth = useMemo(() => generateSizeVariability(width, widthVariability), []);
  const randomHeight = useMemo(() => generateSizeVariability(height, heightVariability), []);
  const resolvedWidth = widthVariability ? randomWidth : width;
  const resolvedWidthCss = typeof resolvedWidth === 'number' ? `${resolvedWidth}px` : resolvedWidth;
  const resolvedPath = sanitizeCssValue(path);
  const skeletonLoaderClassName = cx(
    SKELETON_BASE,
    (context?.shape ?? shape) === 'circle' ? SKELETON_CIRCLE : SKELETON_RECTANGLE,
    (context?.animation ?? animation) === 'shimmer' ? SKELETON_SHIMMER : SKELETON_PULSE,
    className
  );

  const skeletonLoaderStyle = {
    width: resolvedWidth,
    height: isText ? '1em' : heightVariability ? randomHeight : height,
    clipPath: resolvedPath,
    animationDuration: `${context?.duration ?? duration}s`,
  };

  const Component = isText ? 'span' : 'div';

  const skeletonLoader = useMemo(
    () => (
      <>
        {!count ? (
          <Component className={skeletonLoaderClassName} style={{ ...skeletonLoaderStyle, ...style }} {...rest} />
        ) : (
          Array.from({ length: count }).map((_, index) => {
            const isLast = index === count - 1;
            const itemWidth = (isText || !isList) && isLast ? `calc(${resolvedWidthCss} - 1.5em)` : resolvedWidth;
            return (
              <Component
                key={index}
                className={skeletonLoaderClassName}
                style={{
                  ...skeletonLoaderStyle,
                  width: itemWidth,
                  marginBottom: !isLast ? distance : undefined,
                  ...style,
                }}
                {...rest}
              />
            );
          })
        )}
      </>
    ),
    [
      count,
      distance,
      isList,
      isText,
      resolvedWidth,
      resolvedWidthCss,
      resolvedPath,
      rest,
      skeletonLoaderClassName,
      skeletonLoaderStyle,
      style,
    ]
  );

  if (!children) {
    return skeletonLoader;
  }

  return context?.isLoading ?? isLoading ? skeletonLoader : <>{children}</>;
};
